import { ApplicationRef, ComponentRef, inject, Injectable, Signal, signal, WritableSignal } from '@angular/core';
import { Overlay, OverlayConfig, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { OverlayStackComponent } from './internal/overlay-stack.component';
import { OverlayStackItem, RenderedOverlayStackItem } from './overlay-stack-item';

/** Must stay in sync with the fade-out animation durations in the stack component's stylesheet. */
export const FADE_OUT_DURATION_MS = 500;

/**
 * The single overlay hosting every stacked bottom-of-screen message — toasts and notifications alike.
 *
 * Owns the one CDK overlay, the ordered list of items on screen, and their lifetime timers. The
 * {@link ToastService} and {@link NotificationService} facades are the public APIs; they translate
 * their options into {@link OverlayStackItem}s and hand them here.
 */
@Injectable({ providedIn: 'root' })
export class OverlayStackService {
  private readonly overlay: Overlay = inject(Overlay);
  private readonly appRef: ApplicationRef = inject(ApplicationRef);

  private overlayRef?: OverlayRef;
  private stackComponentRef?: ComponentRef<OverlayStackComponent>;

  private readonly itemsSignal: WritableSignal<RenderedOverlayStackItem[]> = signal([]);
  /** Every message on screen, newest first. */
  readonly items: Signal<RenderedOverlayStackItem[]> = this.itemsSignal.asReadonly();

  /** Newer items always sit on top of older ones, even after the older ones are gone. */
  private zIndexCounter = 0;
  private readonly timers = new Map<string, ReturnType<typeof setTimeout>>();

  show(item: OverlayStackItem): void {
    this.attachStackComponent();

    const rendered: RenderedOverlayStackItem = { ...item, zIndex: ++this.zIndexCounter, isShown: true };
    this.itemsSignal.update(items => [rendered, ...items]);

    this.scheduleHide(item.id, item.durationMs);
  }

  /**
   * Replaces what an item shows, keeping its place and stacking order, and restarts its lifetime
   * from the new `durationMs`. No-ops when the item is no longer around.
   */
  update(id: string, item: OverlayStackItem): void {
    const existing = this.itemsSignal().find(candidate => candidate.id === id);

    if (!existing) {
      return;
    }

    const rendered: RenderedOverlayStackItem = { ...item, id, zIndex: existing.zIndex, isShown: true };
    this.itemsSignal.update(items => items.map(candidate => (candidate.id === id ? rendered : candidate)));

    this.clearTimer(id);
    this.scheduleHide(id, item.durationMs);
  }

  /** Fades the item out, then removes it. This is what lifetime timers do; {@link remove} is immediate. */
  hide(id: string): void {
    const existing = this.itemsSignal().find(candidate => candidate.id === id);

    if (!existing) {
      return;
    }

    this.clearTimer(id);
    this.itemsSignal.update(items =>
      items.map(candidate => (candidate.id === id ? { ...candidate, isShown: false } : candidate))
    );

    this.timers.set(
      id,
      setTimeout(() => this.remove(id), FADE_OUT_DURATION_MS)
    );
  }

  /** Removes the item right away — the explicit-dismissal path. No-ops when it is no longer around. */
  remove(id: string): void {
    this.clearTimer(id);
    this.itemsSignal.update(items => items.filter(candidate => candidate.id !== id));
  }

  /** Holds an item's lifetime timer while it is hovered. */
  pauseTimer(id: string): void {
    this.clearTimer(id);
  }

  /** Restarts an item's full lifetime once it is no longer hovered. No-op for permanent items. */
  resumeTimer(id: string): void {
    const item = this.itemsSignal().find(candidate => candidate.id === id);
    this.scheduleHide(id, item?.durationMs);
  }

  private scheduleHide(id: string, durationMs: number | undefined): void {
    if (!durationMs) {
      return;
    }

    this.timers.set(
      id,
      setTimeout(() => this.hide(id), durationMs)
    );
  }

  private clearTimer(id: string): void {
    clearTimeout(this.timers.get(id));
    this.timers.delete(id);
  }

  private attachStackComponent(): void {
    if (this.stackComponentRef) {
      return;
    }

    if (!this.overlayRef) {
      this.overlayRef = this.createOverlayRef();
    }

    this.stackComponentRef = this.overlayRef.attach(new ComponentPortal(OverlayStackComponent));
    this.appRef.attachView(this.stackComponentRef.hostView);
  }

  private createOverlayRef(): OverlayRef {
    const overlayConfig = new OverlayConfig({
      hasBackdrop: false,
      // The pane itself is zero-sized and inert; the stack component positions its containers with
      // `position: fixed`, which is viewport-relative since CDK global wrappers carry no transform.
      positionStrategy: this.overlay.position().global(),
      scrollStrategy: this.overlay.scrollStrategies.noop()
    });

    const overlayRef = this.overlay.create(overlayConfig);
    overlayRef.hostElement.style.zIndex = '10000';
    return overlayRef;
  }
}
